import "./legal-scope.css";

const policies = {
  privacy: {
    eyebrow: "Preview privacy scope",
    title: "Privacy, in plain language.",
    intro: "This scope explains the information Fadius expects to use for its early preview and Watch experience. It is a working draft for counsel, not the final privacy policy.",
    sections: [
      ["What the preview may collect", [
        "Basic technical records from hosting and security providers, such as IP address, browser type, requested pages, timestamps and error logs.",
        "Account details when sign-in is enabled, such as an account identifier, email address, display name and profile image.",
        "A device location reading only when you choose a location-based action, such as finding nearby venues or confirming that you are at a venue.",
        "Structured Watch reports, including the match, venue, time and whether one or multiple screens are showing it. Fadius does not ask for public ratings or reviews.",
      ]],
      ["Bars, venues and supporters groups", [
        "Venue intake may include a contact name, role, business email, optional phone number, venue or group name, city and the soccer competitions usually shown.",
        "A match confirmation records the response, time and intended recipient so Fadius can show, correct or remove the signal.",
        "Contact information is intended for verification and operational follow-up. It is not displayed to fans or used for unrelated marketing.",
      ]],
      ["How Fadius expects to use information", [
        "Operate the preview, show relevant venues and matches, verify reports, prevent misuse, respond to corrections and improve reliability.",
        "Create venue-level and aggregate insights without selling individual location histories.",
        "Measure partner pilots through aggregate actions such as listing views, directions requests, QR visits and verified check-ins. Partner reporting should not identify individual fans.",
      ]],
      ["Service providers and reference data", [
        "Fadius may use hosting, maps, sign-in, forms, security, analytics and error-monitoring providers to operate the service.",
        "Venue and match information may come from venues, fans, public sources or licensed data partners. Fadius will identify required source attribution and keep reference information distinct from a live confirmation.",
        "The final policy will identify the providers actually in use and explain any international data transfers before a public beta.",
      ]],
      ["Retention, deletion and your choices", [
        "Final retention periods for location readings, venue contacts, security records and backups remain subject to founder and counsel approval.",
        "The production service is intended to offer account access, export and deletion. Any limited anti-abuse record retained after deletion must be disclosed and legally reviewed.",
        "The final policy will explain applicable access, correction, deletion, portability and opt-out rights, including California and European rights where they apply.",
      ]],
      ["Children, security and updates", [
        "Fadius is not designed for children under 13. The final age position and store rating must be confirmed before public launch.",
        "Fadius plans to use reasonable technical and organizational safeguards, but no online service can guarantee perfect security.",
        "Material changes will be reflected in the dated final policy and, where appropriate, communicated in the product.",
      ]],
    ],
  },
  terms: {
    eyebrow: "Preview terms scope",
    title: "Use the preview responsibly.",
    intro: "This scope outlines the terms planned for the Fadius early preview and Watch experience. It is a working draft for counsel, not the final Terms of Service.",
    sections: [
      ["Early preview", [
        "Fadius is an early product preview. Features, availability and data may change, fail or be removed.",
        "Sample or illustrative information will be labelled. It must not be treated as a live venue or match confirmation.",
        "Using a preview does not create a commercial partnership with a venue, supporters group, club, league, broadcaster or data provider unless Fadius states otherwise in writing.",
      ]],
      ["Accounts and acceptable use", [
        "When accounts are enabled, users are responsible for their account activity and must provide accurate information.",
        "Users may report only what they actually observe and only when authorized to make the relevant venue or group confirmation.",
        "Location spoofing, automated submissions, duplicate accounts, manipulation of confirmations, interference with the service and unlawful activity are prohibited.",
      ]],
      ["Venue and match information", [
        "Venue hours, affiliations, screen counts and match availability may come from venues, fans or third parties and may be incomplete, stale or wrong.",
        "A venue-confirmed match means the venue said it planned to show the match at the recorded time. Plans can still change, so fans should confirm directly when timing is critical.",
        "Reference venues and historical league or club affinities are not live confirmations and will be presented separately.",
      ]],
      ["Submissions and Fadius rights", [
        "People retain ownership of their original submissions but grant Fadius the limited rights needed to store, display, verify, aggregate and analyze them to operate and improve the service.",
        "Fadius may correct, reject or remove inaccurate, unauthorized, unsafe or abusive submissions and may suspend access for misuse.",
        "The final terms will define the duration and revocability of this license and the treatment of feedback submitted about the product.",
      ]],
      ["Third parties and intellectual property", [
        "Maps, sign-in, venue information, schedules and external links may be supplied by third parties under their own terms.",
        "Fadius does not promise third-party availability and is not responsible for a venue's operations, admission, service, alcohol policies or match programming.",
        "Fadius branding belongs to its operator. Club, league, broadcaster and venue names and marks belong to their respective owners and are used for identification only.",
      ]],
      ["Safety, termination and legal terms", [
        "Do not use Fadius while driving. Follow venue rules, age restrictions and applicable law.",
        "Users may stop using the service and, when accounts are enabled, request deletion. Fadius may restrict access to protect the service or enforce the final terms.",
        "Warranty disclaimers, liability limits, governing law and dispute procedures remain open for counsel and will not be finalized through preview copy alone.",
      ]],
    ],
  },
};

function Brand() {
  return <img src="/brand/downloads/fadius-horizontal-lockup-transparent.png?v=20261023" alt="Fadius" />;
}

export function LegalScopePage({ type }) {
  const policy = policies[type] || policies.privacy;
  const otherType = type === "terms" ? "privacy" : "terms";
  const otherLabel = otherType === "privacy" ? "Privacy scope" : "Terms scope";

  return (
    <main className="legal-page">
      <header className="legal-header">
        <a href="/" aria-label="Back to Fadius"><Brand /></a>
        <a className="legal-back" href="/">Back to preview site</a>
      </header>

      <article className="legal-document">
        <div className="legal-status">Draft scope · Counsel review pending</div>
        <p className="legal-eyebrow">{policy.eyebrow}</p>
        <h1>{policy.title}</h1>
        <p className="legal-intro">{policy.intro}</p>

        <aside className="legal-notice">
          <strong>Current status</strong>
          <span>No final legal entity, effective date, retention schedule, age position, liability terms or governing-law position is stated here. Those items must be completed before this becomes a binding public policy.</span>
        </aside>

        <div className="legal-sections">
          {policy.sections.map(([heading, items], index) => (
            <section key={heading}>
              <div className="legal-section-number">{String(index + 1).padStart(2, "0")}</div>
              <div>
                <h2>{heading}</h2>
                <ul>{items.map((item) => <li key={item}>{item}</li>)}</ul>
              </div>
            </section>
          ))}
        </div>

        <footer className="legal-document-footer">
          <p>Questions or correction requests: <a href="mailto:hello@fadius.app">hello@fadius.app</a></p>
          <nav aria-label="Legal pages"><a href={`/${otherType}`}>{otherLabel}</a><a href="/">Fadius preview site</a></nav>
        </footer>
      </article>
    </main>
  );
}
